import React, { useContext, useEffect, useState } from 'react';
import { Field, SitecoreContext, RichText } from '@sitecore-jss/sitecore-jss-react';

import './index.scss';
import PriceOptions, {
  LanguageOptionProps,
  PriceOptionProps,
} from '../../PriceOptions/PriceOptions';
import CartSlider from '../../Cart/CartSlider/CartSlider';
import { Button } from '@mui/material';
import { getAPICall, postAPICall } from '../../../dataFetcher/axiosMethodCalls';
import LoadingSkeleton from '../../LoadingSkeleton/LoadingSkeleton';
import { ProductDataContext, ProductContextType } from '../../../context/productContext';
import { FormatOptionsFieldsProps } from '../StandardProductCartDetails/StandardProductCartDetails';
import { getCountryConfigFields } from '../../../utils/countryConfigurationHelper';
import { getFieldSpecificConfiguration } from '../../../utils/fieldSpecificConfigHelper';
import { IFieldSpecificConfig } from '../../../model/IFieldSpecificConfig';
import { addCartEvent, CartAnalyticsInfo, checkoutEvent } from '../../../utils/analyticsHelper';
import InstructorLedSessions from '../../InstructorLedSessions/InstructorLedSessions';
import { getCurrencyInUSD } from '../../../utils/helper';
import { LoadingIcon } from '../../LoadingIcon/LoadingIcon';
import { APIWebstoreContext } from '../../../context/globalContext';
import { Navigate } from 'react-router-dom';
import { ProductType } from '../../../utils/ProductTypes';

export type ProductSalesPriceProps = {
  fields: {
    SalesPriceNote: Field<string>;
    UpcomingSessionsNote: Field<string>;
    NoSessionsAvailableText: Field<string>;
    Countries: Array<ProductPriceOptionsFieldProps>;
    FormatOptions: Array<FormatOptionsFieldsProps>;
    FieldConfig: Array<IFieldSpecificConfig>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  config: {
    hasPriceOptions: boolean;
    hasSessionOptions: boolean;
    hasCountryRestrictions: boolean;
  };
};

export type ProductPriceOptionsFieldProps = {
  displayName: string;
  fields: IPriceOptionFields;
};

export interface IPriceOptionFields {
  CountryCode: Field<string>;
  CountryName: Field<string>;
  HidePDFProducts: Field<boolean>;
  HidePrintProducts: Field<boolean>;
}

export interface SessionOptions {
  sessionName: string;
  sessionId: string;
  title: string;
  specId: string;
  optionId: string;
  startDate: string;
  endDate: string;
  timezone: string;
  language: string;
  unitPrice: number;
  quantity: number;
  description: string;
  maxQuantity: number;
  capacityAvailable: number;
}

const ProductSalesPrice = (props: ProductSalesPriceProps): React.JSX.Element => {
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [languageOptions, setLanguageOptions] = useState(Array<LanguageOptionProps>());
  const [sessionOptions, setSessionOptions] = useState<SessionOptions[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [productTitle, setProductTitle] = useState<string>('');
  const [edition, setEdition] = useState<string>('');
  const [salesPrice, setSalesPrice] = useState(Array<PriceOptionProps>());
  const [openDrawer, setOpenDrawer] = useState(false);
  const [resetPrice, setResetPrice] = useState('reset');
  const [expandMode, setExpandMode] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProductActive, setIsProductActive] = useState(true);
  const { productDetail } = useContext(ProductDataContext) as ProductContextType;
  const [countryConfigurationInfo, setCountryConfigurationInfo] = useState<IPriceOptionFields>();
  const [fieldSpecificConfig, setFieldSpecificConfig] = useState<IFieldSpecificConfig>();
  const [selectedSession, setSelectedSession] = useState<SessionOptions | null>(null);
  const [isAddToCart, setIsAddToCart] = useState(false);
  const [isAddToCartAndCheckout, setIsAddToCartAndCheckout] = useState(false);
  const [redirectToCheckout, setRedirectToCheckout] = useState(false);
  const [isItemsUpdated, setIsItemsUpdated] = useState(false);

  const handleClick = (expand: boolean) => {
    calculateTotal();
    setExpandMode(expand);
    setOpenDrawer(true);
    setResetPrice('unset');
  };

  const handleClose = () => {
    setOpenDrawer(false);
    salesPrice.map((item: PriceOptionProps) => {
      item.quantity = 0;
    });
    languageOptions.map((salesPrice: LanguageOptionProps) => {
      salesPrice.priceOptions.map((item: PriceOptionProps) => {
        item.quantity = 0;
      });
    });
    setResetPrice('reset');
  };

  const getFieldSpecificConfig = (productType: string) => {
    let fieldConfigData = getFieldSpecificConfiguration(productType, props.fields.FieldConfig);
    const fieldSpecificConfiguration: IFieldSpecificConfig | undefined = fieldConfigData.map(
      (config) => {
        return {
          CartItemMaxQuantity: config.fields.CartItemMaxQuantity,
        };
      }
    )[0];
    return fieldSpecificConfiguration;
  };

  function toggleAccordion() {
    setExpandMode((prev) => !prev);
  }

  function renderWithLoading(value: any, theme?: any, loaderWidth?: any, loaderHeight?: any) {
    return isLoading ? (
      <LoadingSkeleton theme={theme} loaderWidth={loaderWidth} loaderHeight={loaderHeight} />
    ) : (
      value
    );
  }

  const calculateTotal = () => {
    if (languageOptions) {
      setSubTotal(
        languageOptions.reduce((accumulator, options) => {
          return options.priceOptions.reduce(
            (accumulator, price) => (accumulator += price.quantity * price.unitPrice),
            accumulator
          );
        }, 0)
      );
    }
  };

  function checkQuantityCountWithOptions(langOptions: Array<LanguageOptionProps>) {
    const isItemsAdded = langOptions?.some((options) =>
      options.priceOptions?.some((price) => price.quantity > 0)
    );
    setIsItemsUpdated(isItemsAdded);
  }

  function calculateTotalWithOptions(langOptions: Array<LanguageOptionProps>) {
    const total = langOptions.reduce((accumulator, options) => {
      return options.priceOptions.reduce(
        (acc, price) => acc + price.quantity * price.unitPrice,
        accumulator
      );
    }, 0);
    setSubTotal(total);
  }

  function getSalesPrice(
    productId: string,
    productType: string,
    edition: string,
    year: string,
    term: string,
    hasPriceOptions: boolean
  ) {
    let productSalePriceAPIURL = `${process.env.REACT_APP_CATALOG_URL}/catalog/price/${productId}/${productType}/`;
    let previousEdition;
    if (productType === ProductType.StatisticsReport) {
      productSalePriceAPIURL += `${year}/${term}`;
    } else {
      previousEdition = edition && edition.length > 0 ? `${edition}` : '';
      productSalePriceAPIURL += `${previousEdition}`;
    }

    getAPICall(productSalePriceAPIURL)
      .then(function (response: any) {
        if (response && response.data) {
          const languageOptionsData = response.data.languageOptions || [];
          setLanguageOptions(languageOptionsData);
          setProductId(response.data.productId);
          setEdition(response.data.edition);
          setProductTitle(response.data.title);
          setIsProductActive(response.data.isProductActive);
          setSessionOptions(response.data.sessionOptions);
          let defautPriceOptions = response.data.languageOptions?.filter(
            (x: { isDefault: boolean }) => x.isDefault
          )[0].priceOptions;
          if (hasPriceOptions) {
            setSalesPrice([...defautPriceOptions]);
          } else {
            const priceOptions: PriceOptionProps[] = [
              {
                unitPrice: response.data.unitPrice,
                quantity: response.data.quantity || 1,
                isActive: response.data.isProductActive,
              },
            ];
            setSalesPrice(priceOptions);
            setLanguageOptions([
              {
                productType,
                priceOptions: priceOptions,
                isDefault: false,
              },
            ]);
            setIsItemsUpdated(true);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (productDetail?.productId && productDetail?.productId.length > 0) {
      getSalesPrice(
        productDetail?.productId,
        productDetail?.productType || 'standard',
        productDetail?.edition || '',
        productDetail?.year || '',
        productDetail?.term || '',
        props.config?.hasPriceOptions
      );
      setFieldSpecificConfig(getFieldSpecificConfig(productDetail?.productType || ''));
    }
  }, [productDetail?.productId, productDetail?.edition, productDetail?.year, productDetail?.term]);

  useEffect(() => {
    getShippingCountry();
  }, []);

  useEffect(() => {
    if (languageOptions?.length > 0) {
      checkQuantityCountWithOptions(languageOptions);
      calculateTotalWithOptions(languageOptions);
    }
  }, [languageOptions]);

  function AddToCart(redirect: boolean) {
    const orderLineItems = languageOptions?.flatMap((languageOption) =>
      languageOption.priceOptions
        .filter((priceOption) => priceOption.quantity > 0)
        .map((priceOption) => ({
          productId,
          variantId: priceOption.variantId,
          quantity: priceOption.quantity,
          specId: selectedSession?.specId,
          optionId: selectedSession?.optionId,
        }))
    );

    const addToCartRequest = {
      OrderLineItems: orderLineItems,
      LOB: productDetail?.productMetaData?.lob,
    };

    postAPICall(
      `${process.env.REACT_APP_CART_URL}/cartOrder/unifiedcreateorder`,
      addToCartRequest
    ).then(function (response: any) {
      if (response) {
        setIsAddToCart(false);
        if (dispatch) {
          dispatch({
            type: 'CART_UPDATE',
            payload: {
              toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            },
          });
        }
        if (redirect) {
          setIsAddToCartAndCheckout(false);
          setRedirectToCheckout(true);
        }
      }
    });
  }

  const handleAddToCart = (e: any) => {
    e.preventDefault();
    setIsAddToCart(true);
    AddToCart(false);
    formJSONAndPushToGTM();
  };

  const handleAddToCartAndCheckout = (e: any) => {
    e.preventDefault();
    formJSONAndPushToGTM();

    if (isItemsUpdated) {
      setIsAddToCartAndCheckout(true);
      AddToCart(true);
    } else {
      setRedirectToCheckout(true);
    }
  };

  const formJSONAndPushToGTM = () => {
    let value: any;
    value = subTotal.toFixed(2);
    const itemAnalyticsInfo: CartAnalyticsInfo = {
      currency: 'USD',
      value: value,
      items: [],
    };
    if (languageOptions) {
      languageOptions.filter(function (langOptions) {
        langOptions?.priceOptions?.filter(function (priceOptions) {
          let price: any = priceOptions?.unitPrice;
          price = price.toFixed(2);
          if (priceOptions.quantity > 0) {
            itemAnalyticsInfo.items.push({
              item_id: `${productId}`,
              item_name: `${productTitle}`,
              item_brand: `${productDetail?.productType}`,
              item_variant: priceOptions?.variantId ?? '',
              price: price,
              quantity: priceOptions?.quantity,
            });
          }
        });
      });
    }

    addCartEvent(itemAnalyticsInfo);
  };

  function disableCheckoutButton() {
    if (state?.cartCounts?.totalCount === 0 && !isItemsUpdated) {
      return true;
    }
    return false;
  }

  function showGoToCheckoutButton() {
    if (disableCheckoutButton() || (state?.cartCounts?.totalCount !== 0 && !isItemsUpdated)) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
    const defaultLang = languageOptions?.find((x) => x.isDefault);
    if (defaultLang) {
      setSalesPrice([...defaultLang.priceOptions]);
    }
  }, [languageOptions]);

  function getShippingCountry() {
    let shippingCountry: string = '';
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`)
      .then(function (response: any) {
        if (response && response.data) {
          shippingCountry = response.data;
        }
      })
      .finally(() => {
        setCountryConfigurationInfo(getCountryConfigurationData(shippingCountry));
      });
  }

  function getCountryConfigurationData(shippingCountry: string) {
    let countryConfigurationData: Array<ProductPriceOptionsFieldProps> = [];

    countryConfigurationData = getCountryConfigFields(shippingCountry, props.fields.Countries);
    const countryConfigurationFields: IPriceOptionFields | undefined = countryConfigurationData.map(
      (config) => {
        return {
          CountryCode: config.fields.CountryCode,
          CountryName: config.fields.CountryName,
          HidePrintProducts: config.fields.HidePrintProducts,
          HidePDFProducts: config.fields.HidePDFProducts,
        };
      }
    )[0];

    return countryConfigurationFields;
  }

  return (
    <div className="productSalesPrice">
      {props.config.hasCountryRestrictions && (
        <div className="salesPriceNote">
          <RichText className="NoteArea" field={props.fields?.SalesPriceNote} />
        </div>
      )}
      <div className="salesPriceOuterContainer">
        <div className="salesPriceLeftWidth"></div>

        <div className="salesPriceColumnAlignSessions">
          {props.config.hasSessionOptions && (
            <InstructorLedSessions
              sessions={sessionOptions}
              title={productTitle}
              upcomingSessionsNote={props.fields?.UpcomingSessionsNote?.value}
              NoSessionsAvailableText={props.fields?.NoSessionsAvailableText?.value}
              onSessionSelect={(session) => {
                setSelectedSession(session);
              }}
            />
          )}

          {(!props.config?.hasSessionOptions || sessionOptions?.length > 0) && (
            <div className="salesPriceColumnAlign">
              {renderWithLoading(
                <PriceOptions
                  priceOptions={salesPrice}
                  reset={resetPrice}
                  key={resetPrice}
                  isProductActive={isProductActive}
                  formatType={productDetail?.productType}
                  formatOptions={props.fields?.FormatOptions}
                  countryConfigFields={countryConfigurationInfo}
                  productType={productDetail?.productType || ''}
                  fieldConfig={fieldSpecificConfig}
                  calculateTotal={() => calculateTotalWithOptions(languageOptions)}
                  checkQuantityCount={() => checkQuantityCountWithOptions(languageOptions)}
                />,
                'light',
                '100%',
                '20rem'
              )}

              {languageOptions?.length > 1 && (
                <div className="availablelanglink">
                  <Button variant="outlined" color="primary" onClick={() => handleClick(true)}>
                    Available in other languages
                  </Button>
                </div>
              )}
              {!languageOptions?.find((x) => x.isDefault) ? (
                <div className="cartNavigationLinkSection">
                  <p>
                    <span className="cartSubTotalTitle">Cart Subtotal</span>
                    <span className="cartSubTotalValue">
                      {subTotal ? getCurrencyInUSD(subTotal) : getCurrencyInUSD(0)}
                    </span>
                  </p>
                  <div className="cartNavButtons">
                    <a
                      className={`addtoCartButton ${
                        isItemsUpdated ? '' : 'add-to-cart-btn-disable'
                      } `}
                      href="#"
                      {...(!isAddToCart ? { onClick: handleAddToCart } : {})}
                    >
                      {isAddToCart ? (
                        <LoadingIcon />
                      ) : (
                        state?.sitecoreDictionaryData?.ADD_TO_CART || 'ADD TO CART'
                      )}
                    </a>
                    <a
                      className={
                        !disableCheckoutButton()
                          ? 'addtoCartButton addtoCartAndCheckoutButton'
                          : 'addtoCartButton addtoCartAndCheckoutButton add-to-cart-btn-disable'
                      }
                      href="#"
                      {...(!isAddToCartAndCheckout ? { onClick: handleAddToCartAndCheckout } : {})}
                    >
                      {isAddToCartAndCheckout ? (
                        <LoadingIcon />
                      ) : showGoToCheckoutButton() ? (
                        state?.sitecoreDictionaryData?.GO_TO_CHECKOUT || 'GO TO CHECKOUT'
                      ) : (
                        state?.sitecoreDictionaryData?.ADD_TO_CART_AND_CHECKOUT ||
                        'ADD TO CART AND CHECKOUT'
                      )}
                      {redirectToCheckout ? <Navigate to="/orders/checkout" replace /> : null}
                    </a>
                  </div>
                </div>
              ) : (
                <div className="productAddToCart">
                  {renderWithLoading(
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={() => handleClick(false)}
                    >
                      ADD TO CART
                    </Button>,
                    'light',
                    '10rem',
                    '5rem'
                  )}
                </div>
              )}

              <CartSlider
                languageOptions={languageOptions}
                productId={productId}
                productType={productDetail?.productType || 'standard'}
                productTitle={productTitle}
                edition={edition}
                openSlider={openDrawer}
                closeSlider={handleClose}
                expandMode={expandMode}
                toggleAccordion={toggleAccordion}
                isProductActive={isProductActive}
                countryConfigFields={countryConfigurationInfo}
                formatOptions={props.fields?.FormatOptions}
                fieldSpecificConfig={fieldSpecificConfig}
                selectedSession={selectedSession}
                lob={productDetail?.productMetaData?.lob}
              ></CartSlider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSalesPrice;
